import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {ComponentHelper, Debug} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {InputMask} from "../../misc/addon/inputmask/InputMask";
import {InputMaskProps} from "../../misc/addon/inputmask/InputMask.model";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";
import {InputText} from "@bizform/primereact/inputtext";


type Props = InputMaskProps & {
};

type WrappedCompState = {
    // attribute
    value?: string | number;
    type?: string;
    mask?: string;
    slotChar?: string;
    autoClear?: boolean;
    // unmask?: boolean;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    placeholder?: string;
    size?: number;
    maxlength?: number;
    tabindex?: number;
    disabled?: boolean;
    readOnly?: boolean;
    name?: string;
    required?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};
    // isDateTime?: boolean;
    useUnmask?: boolean;
    accessibility?: boolean;
    // event
    onChange?(e: ComponentEventType): void;
    onComplete?(e: Event): void;

};

type WrappedFormState = {
    onChange?: string;
    onComplete?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, modified by Chris
 * @class BfInputMask
 * @description BfInputMask component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string | number>>()
export class BfInputMask extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_1D1MfRnxCwEP9TgAQOAEUJnsAK6XGwP",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.inputmask.label,
        description: I18n.componentElement.inputmask.description,
        icon: "",
        dataType: [DataType.STRING, DataType.NUMBER, DataType.INTEGER],
        property: null
    };

    focus(): void {
        if (this._inputMask && this._inputMask.input) {
            this._inputMask.input.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string | number): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string | number): void {
        if (ComponentHelper.isNull(value) || (typeof value === "number" && Number.isNaN(value))) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: ""
                }
            };
            this.setState(newState);
        } else if ( typeof value === "object") {
            this.emitSubscriptionReject();
        } else {
            this._subFlag = true;
            this._focusFlag = false;
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfInputMask)
    static init(): ComponentElement<string | number> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _inputMask: any;
    private _inputText: any;
    private _tooltip: Array<any> = [];
    private _subFlag: boolean;
    private _focusFlag: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._onCompleteEvent = this._onCompleteEvent.bind(this);
        this._focusHandler = this._focusHandler.bind(this);
        this._blurHandler = this._blurHandler.bind(this);
        this._accessibilityChangeEvent = this._accessibilityChangeEvent.bind(this);
        this._setTextRef = this._setTextRef.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(): void {
        if (this._inputMask && this._inputMask.input) {
            ComponentHelper.tooltip(this._inputMask.input, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
        if (this._focusFlag === true) {
            this.focus();
        }
        // control input Mask value for print mode and masked subscription value
        const maskedValue = this._inputMask && this._inputMask.input && String(this._inputMask.getUnmaskedValue()).length === String(this.state.wrappedCompState.value).length ? this._inputMask.input.value : String(this.state.wrappedCompState.value);
        if (!this.accessibility) {
            if (this.state.displayMode === ComponentElementDisplayMode.PRINT && this._inputText && this._inputMask && this._inputMask.input) {
                this._inputText.innerText = this._inputMask.input.value;
            }
            if (this._subFlag === true && this._inputText) {
                this._subFlag = false;
                if (this.state.displayMode === ComponentElementDisplayMode.PRINT) {
                    this._inputText.innerText = maskedValue;
                } else if (this._inputText.element) {
                    this._inputText.element.value = maskedValue;
                }
            }
        } else if (this.state.displayMode === ComponentElementDisplayMode.PRINT && this._inputText) {
            this._inputText.innerText = maskedValue;
        }
    }

    componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
        this._removeCustom();
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._inputMask = null;
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return true;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: e.value
            }
        };

        this.setState(newState, this._callbackSetState);
    }

    private _accessibilityChangeEvent(event) {
        if (event.target.value !== this.state.wrappedCompState.value) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: event.target.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _onCompleteEvent(e: ComponentEventType): void {
    }

    private _callbackSetState() {
        if (this.accessibility) {
            this.emit(this.state.wrappedCompState.value);
        } else {
            if (!(this._inputMask)) { return null; }
            if (this.dataType && (this.dataType === DataType.NUMBER || this.dataType === DataType.INTEGER)) {
                this.emit(this._inputMask.getUnmaskedValue());
            } else {
                if (this.state.wrappedCompState.useUnmask) {
                    this.emit(this._inputMask.getUnmaskedValue());
                } else {
                    this.emit(this._inputMask.input.value);
                }
            }
        }
    }

    private _focusHandler(e?) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            e.stopPropagation();
            e.preventDefault();
            this._subFlag = false;
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: this.state.wrappedCompState.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _blurHandler() {
        this._focusFlag = false;
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }
    private _render(newProps: Props): React.ReactNode {
        let text, mask;
        if (this._subFlag === true && this._inputMask) {
            text = "block";
            mask = "none";
        } else {
            text = "none";
            mask = "block";
        }
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <InputText
                    id={this.elementId}
                    key={this.elementId}
                    ref={this._setTextRef}
                    className={newProps.className}
                    style={newProps.style}
                    readOnly={this.state.displayMode === ComponentElementDisplayMode.READONLY}
                    disabled={this.state.displayMode === ComponentElementDisplayMode.DISABLED}
                    value={newProps.value}
                    onComplete={this._onCompleteEvent}
                    onChange={this._accessibilityChangeEvent}
                />
               );
        } else {
            // Input Text for masked subscription value, the value made from inputMask, this value adapted at componentDidUpdate
            return (
                <React.Fragment>
                    <span style= {{display: `${text}`}}>
                        <InputText
                            key={this.elementId}
                            ref={this._setTextRef}
                            className={newProps.className}
                            style={newProps.style}
                            value={newProps.value}
                            onFocus={this._focusHandler}
                            onBlur={this._blurHandler}
                            readOnly={this.state.displayMode === ComponentElementDisplayMode.READONLY}
                            disabled={this.state.displayMode === ComponentElementDisplayMode.DISABLED}
                        />
                    </span>
                    <span style= {{display: `${mask}`}}>
                        <InputMask
                            {...newProps}
                            key={this.elementId}
                            id={this.elementId}
                            ref={this._setRef}
                            unmask={false}
                            autoClear={false}
                            accessibility={this.accessibility}
                            readOnly={this.state.displayMode === ComponentElementDisplayMode.READONLY}
                            disabled={this.state.displayMode === ComponentElementDisplayMode.DISABLED}
                        />
                    </span>
                </React.Fragment>
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };
        delete(newProps.printStyle);
        delete(newProps.printClassName);
        return (
            <div ref={this._setTextRef} style={this.state.wrappedCompState ? {height: "30px", ...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>
                <InputMask
                    {...newProps}
                    style={{display: "none"}}
                    key={this.elementId}
                    id={this.elementId}
                    ref={this._setRef}
                    unmask={false}
                    autoClear={false}
                    accessibility={this.accessibility}
                />
            </div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._inputMask = ref;
            this.readyComponent();
        }
    }
    private _setTextRef(ref) {
        if (ref) {
            this._inputText = ref;
        }
    }

    private _removeCustom(): void {
        ComponentHelper.unbindTooltip(this._tooltip);
    }
}